import { Box, IconButton } from "@mui/material"
import { DeleteOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpFilledOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import type { FC } from "react"
import { useState } from "react"

import { useActionsColumn } from "../hooks/useActionsColumn"

import { CancelDialog } from "@/components/common/Modals"
import type { OrderLine } from "@/graphql/codegen/graphql"
import { usePurchaseOrderReadonly } from "@/screens/Procurement/PurchaseOrder/hooks/usePurchaseOrderReadonly"

interface Props {
  row: Row<OrderLine>
  handleRecalculateDeliveryTerms?: () => void
}

export const ActionsCell: FC<Props> = ({ row, handleRecalculateDeliveryTerms }) => {
  const { onRemoveRow, onCancelPO, multipleProductsPO } = useActionsColumn({
    row,
    handleRecalculateDeliveryTerms,
  })
  const { isReadonly } = usePurchaseOrderReadonly()
  const [warning, setWarning] = useState<WarningData>()

  const closeWarningDialog = () => {
    setWarning(undefined)
  }

  const removeProduct = () => {
    const warningType = multipleProductsPO
      ? WARNINGS[WARNING_TYPES.DELETE_PRODUCT]
      : WARNINGS[WARNING_TYPES.DELETE_LAST_PRODUCT]

    setWarning(warningType)
  }

  const WARNINGS: Record<WARNING_TYPES, WarningData> = {
    [WARNING_TYPES.DELETE_PRODUCT]: {
      title: `Are you sure you want to delete this product\n ID ${row.original.product?.publicId || row.original.productId} ?`,
      message: "This action will affect the order total quantities and total amount.",
      cancelText: "Yes, Delete",
      goBackText: "Cancel",
      confirm: () => {
        onRemoveRow()
        closeWarningDialog()
      },
    },
    [WARNING_TYPES.DELETE_LAST_PRODUCT]: {
      title: "You are trying to delete the only product on this PO. Instead of deleting it, the PO will be cancelled.",
      message:
        'Click on "Cancel PO" if you agree or "Go Back" to add other products before deleting the only one added.',
      cancelText: "Cancel PO",
      goBackText: "Go Back",
      confirm: () => {
        onCancelPO()
        closeWarningDialog()
      },
    },
  }

  return (
    <Box className='flex items-center justify-end gap-4 space-x-4'>
      <IconButton
        className='p-0'
        size='small'
        aria-label='delete the entire row'
        onClick={removeProduct}
        disabled={isReadonly}
      >
        <DeleteOutlined />
      </IconButton>

      <IconButton aria-label='expand row' size='small' onClick={() => row.toggleExpanded()} data-testid='expand-button'>
        {row.getIsExpanded() ? <KeyboardArrowUpFilledOutlined /> : <KeyboardArrowDownOutlined />}
      </IconButton>

      {warning ? (
        <CancelDialog
          onClose={closeWarningDialog}
          onConfirm={warning.confirm}
          title={warning.title}
          message={warning.message}
          closeLabel={warning.goBackText}
          confirmLabel={warning.cancelText}
        />
      ) : null}
    </Box>
  )
}

enum WARNING_TYPES {
  DELETE_PRODUCT = "deleteProduct",
  DELETE_LAST_PRODUCT = "deleteLastProduct",
}

interface WarningData {
  title: string
  message: string
  cancelText: string
  goBackText: string
  confirm: () => void
}
